import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { GET_ROADS } from '../gql/queries/geoPositions/roads';

type RoadWithConcessions = { id?: string | null; concessionIDs?: string[] | null };

/**
 * Las vías donde una patrulla puede APOYAR fuera de su recorrido: las de su
 * concesión que el recorrido no declara (2026-09-23).
 *
 * La concesión es la frontera por el mismo motivo que en el servidor
 * (`RouteSegmentService.supportRoadIds`): es la que filtra las vías en el
 * asistente, y es la única que el servidor acepta añadir. Ofrecer una vía de
 * otra concesión —la malla de la tablet las trae todas, las del tenant entero—
 * haría que el tramo cayera a la primera vía del recorrido al sincronizar, con
 * un PK de otra carretera: exactamente el dato malo que esto viene a evitar.
 *
 * Pura para poder probarla sin Apollo; el hook de abajo solo la alimenta.
 */
export const supportRoadCandidates = (
  roads: RoadWithConcessions[] | null | undefined,
  routeRoadIds: string[] | null | undefined,
  concessionIds: string[] | null | undefined
): string[] => {
  const ownRoadIds = new Set(routeRoadIds ?? []);
  const concessionSet = new Set(concessionIds ?? []);
  if (concessionSet.size === 0) return [];
  return (roads ?? [])
    .filter(
      (road): road is { id: string; concessionIDs?: string[] | null } =>
        !!road?.id &&
        !ownRoadIds.has(road.id) &&
        (road.concessionIDs ?? []).some((id) => concessionSet.has(id))
    )
    .map((road) => road.id);
};

/**
 * `cache-first` con `errorPolicy: 'ignore'`, como el resto de hooks de
 * carreteras de los modales del cronómetro: sin cobertura y con la caché fría
 * no hay candidatas, y eso es exactamente el comportamiento de antes —nunca
 * peor—.
 */
export const useSupportRoadCandidates = (
  routeRoadIds: string[] | null | undefined,
  concessionIds: string[] | null | undefined
): string[] => {
  const { data } = useQuery(GET_ROADS, {
    skip: !concessionIds?.length,
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });
  const roads = data?.roads as RoadWithConcessions[] | undefined;
  return useMemo(
    () => supportRoadCandidates(roads, routeRoadIds, concessionIds),
    // Los arrays llegan nuevos en cada render: se compara el contenido.
    [roads, (routeRoadIds ?? []).join(','), (concessionIds ?? []).join(',')]
  );
};

export default useSupportRoadCandidates;
